import http from "@/helper/http";

const initialState = () => ({
  plans: [],
  loadingPlans: false,
  loadingStoreStatus: false,
  loadingGroupStatus: false,
  storeStatus: null,
  storeStatusError: "",
  groupStatus: null,
  groupStatusError: "",
  lastStoreAssignment: null,
  lastGroupAssignment: null,
});

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    SET_PLANS(state, plans) {
      state.plans = plans || [];
    },
    SET_LOADING_PLANS(state, loading) {
      state.loadingPlans = loading;
    },
    SET_STORE_STATUS_LOADING(state, loading) {
      state.loadingStoreStatus = loading;
    },
    SET_GROUP_STATUS_LOADING(state, loading) {
      state.loadingGroupStatus = loading;
    },
    SET_STORE_STATUS(state, payload) {
      state.storeStatus = payload || null;
    },
    SET_GROUP_STATUS(state, payload) {
      state.groupStatus = payload || null;
    },
    SET_STORE_STATUS_ERROR(state, message) {
      state.storeStatusError = message || "";
    },
    SET_GROUP_STATUS_ERROR(state, message) {
      state.groupStatusError = message || "";
    },
    SET_LAST_STORE_ASSIGNMENT(state, payload) {
      state.lastStoreAssignment = payload || null;
    },
    SET_LAST_GROUP_ASSIGNMENT(state, payload) {
      state.lastGroupAssignment = payload || null;
    },
    RESET_STATE(state) {
      Object.assign(state, initialState());
    },
  },
  actions: {
    async fetchPlans({ commit }) {
      commit("SET_LOADING_PLANS", true);
      try {
        const response = await http.get("/v1/subscription/plans");
        commit("SET_PLANS", Array.isArray(response?.data) ? response.data : []);
      } finally {
        commit("SET_LOADING_PLANS", false);
      }
    },
    async createPlan(_, payload) {
      const response = await http.post("/v1/admin/subscription/plans", payload);
      return response?.data;
    },
    async updatePlan(_, { planId, payload }) {
      const response = await http.patch(
        `/v1/admin/subscription/plans/${planId}`,
        payload
      );
      return response?.data;
    },
    async fetchStoreSubscriptions(_, params) {
      const response = await http.get("/v1/admin/subscriptions/stores", {
        params,
      });
      return response?.data;
    },
    async fetchGroupSubscriptions(_, params) {
      const response = await http.get("/v1/admin/subscriptions/groups", {
        params,
      });
      return response?.data;
    },
    async assignStoreSubscription({ commit }, { storeId, body }) {
      const response = await http.post(
        `/v1/admin/subscriptions/store/${storeId}`,
        body
      );
      commit("SET_LAST_STORE_ASSIGNMENT", response?.data || null);
      return response?.data;
    },
    async assignGroupSubscription({ commit }, { groupId, body }) {
      const response = await http.post(
        `/v1/admin/subscriptions/group/${groupId}`,
        body
      );
      commit("SET_LAST_GROUP_ASSIGNMENT", response?.data || null);
      return response?.data;
    },
    async fetchStoreStatus({ commit }, storeId) {
      commit("SET_STORE_STATUS_LOADING", true);
      commit("SET_STORE_STATUS_ERROR", "");
      try {
        // Use admin endpoint for fresh, uncached data
        const response = await http.get(
          `/v1/admin/stores/${storeId}/subscription`
        );
        commit("SET_STORE_STATUS", response?.data || null);
      } catch (error) {
        commit(
          "SET_STORE_STATUS_ERROR",
          error?.response?.data?.error?.message ||
            "Unable to load store subscription."
        );
        commit("SET_STORE_STATUS", null);
        throw error;
      } finally {
        commit("SET_STORE_STATUS_LOADING", false);
      }
    },
    async fetchGroupStatus({ commit }, groupId) {
      commit("SET_GROUP_STATUS_LOADING", true);
      commit("SET_GROUP_STATUS_ERROR", "");
      try {
        // Use admin endpoint for fresh, uncached data
        const response = await http.get(
          `/v1/admin/groups/${groupId}/subscription`
        );
        commit("SET_GROUP_STATUS", response?.data || null);
      } catch (error) {
        commit(
          "SET_GROUP_STATUS_ERROR",
          error?.response?.data?.error?.message ||
            "Unable to load group subscription."
        );
        commit("SET_GROUP_STATUS", null);
        throw error;
      } finally {
        commit("SET_GROUP_STATUS_LOADING", false);
      }
    },
    async checkStoreFeature(_, { storeId, feature }) {
      const response = await http.get(
        `/v1/stores/${storeId}/subscription/feature/${feature}`
      );
      return response?.data || null;
    },
    async checkGroupFeature(_, { groupId, feature }) {
      const response = await http.get(
        `/v1/groups/${groupId}/subscription/feature/${feature}`
      );
      return response?.data || null;
    },
  },
  getters: {
    plans: (state) => state.plans,
    loadingPlans: (state) => state.loadingPlans,
    storeStatus: (state) => state.storeStatus,
    storeStatusLoading: (state) => state.loadingStoreStatus,
    storeStatusError: (state) => state.storeStatusError,
    groupStatus: (state) => state.groupStatus,
    groupStatusLoading: (state) => state.loadingGroupStatus,
    groupStatusError: (state) => state.groupStatusError,
    lastStoreAssignment: (state) => state.lastStoreAssignment,
    lastGroupAssignment: (state) => state.lastGroupAssignment,
  },
};
